import type { Result } from 'true-myth';

import type { Job, JobPaginated } from '~/shared/data-contract/job';
import { jobPaginatedSchema, jobSchema } from '~/shared/data-contract/job';
import type { JobLevel } from '~/shared/data-contract/job-level';
import { jobLevelWithTypeSchemaTransformed } from '~/shared/data-contract/job-level';
import type { JobRole } from '~/shared/data-contract/job-role';
import { jobRoleWithTypeSchemaTransformed } from '~/shared/data-contract/job-role';
import type { EndorsedHardSoftSkill, SkillShort } from '~/shared/data-contract/skill';
import { endorsedHardSoftSkillArraySchema, skillShortSchema } from '~/shared/data-contract/skill';
import type { PaginatedUsers, User } from '~/shared/data-contract/user';
import { userPaginationSchema, userSchema } from '~/shared/data-contract/user';
import type { HttpFetcherError } from '~/shared/http-fetcher/http-fetcher';
import type { LanguageCode } from '~/shared/internationalization';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

import type { GlobalSearchJobResponse, GlobalSearchUserResponse } from '../../data-contract/global-search';
import { globalSearchJobResponseSchema, globalSearchUserResponseSchema } from '../../data-contract/global-search';

type GraphCanvasPage = {
    readonly page: number;
    readonly size: number;
};

export type GraphCanvasFetchers = {
    readonly fetchGraphCanvasJob: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly jobId: string;
    }) => Promise<Result<Job, HttpFetcherError>>;
    readonly fetchGraphCanvasJobLevels: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly architectureId: string;
        readonly roleId: string;
    }) => Promise<Result<readonly JobLevel[], HttpFetcherError>>;
    readonly fetchGraphCanvasJobRoles: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly architectureId: string;
        readonly clusterId: string;
    }) => Promise<Result<readonly JobRole[], HttpFetcherError>>;
    readonly fetchGraphCanvasJobs: (
        args: GraphCanvasPage & { readonly acceptLanguage: LanguageCode },
    ) => Promise<Result<JobPaginated, HttpFetcherError>>;
    readonly fetchGraphCanvasRelatedSkills: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly limit: number;
        readonly skillId: string;
    }) => Promise<Result<readonly SkillShort[], HttpFetcherError>>;
    readonly fetchGraphCanvasUser: (args: { readonly userId: string }) => Promise<Result<User, HttpFetcherError>>;
    readonly fetchGraphCanvasUsers: (args: GraphCanvasPage) => Promise<Result<PaginatedUsers, HttpFetcherError>>;
    readonly fetchGraphCanvasUserSkills: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly userId: string;
    }) => Promise<Result<readonly EndorsedHardSoftSkill[], HttpFetcherError>>;
    readonly searchGraphCanvasJobs: (
        args: GraphCanvasPage & { readonly searchTerm: string },
    ) => Promise<Result<GlobalSearchJobResponse, HttpFetcherError>>;
    readonly searchGraphCanvasSkills: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly limit: number;
        readonly searchTerm: string;
    }) => Promise<Result<readonly SkillShort[], HttpFetcherError>>;
    readonly searchGraphCanvasUsers: (
        args: GraphCanvasPage & { readonly searchTerm: string },
    ) => Promise<Result<GlobalSearchUserResponse, HttpFetcherError>>;
};

export type GraphCanvasFetchersDependencies = {
    readonly validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

export const createGraphCanvasFetchers = (dependencies: GraphCanvasFetchersDependencies): GraphCanvasFetchers => {
    const { validatingCobrainerApiFetcher } = dependencies;

    const fetchGraphCanvasRelatedSkills: GraphCanvasFetchers['fetchGraphCanvasRelatedSkills'] = async ({
        acceptLanguage,
        limit,
        skillId,
    }) => {
        return validatingCobrainerApiFetcher.postJson(skillShortSchema.array(), {
            pathname: '/skills/related',
            payload: {
                hardSkillsLimit: limit,
                languageSkillsLimit: limit,
                skills: [{ id: skillId, level: 0 }],
                softSkillsLimit: limit,
            },
            requestOptions: { headers: { 'Accept-Language': acceptLanguage } },
        });
    };

    const searchGraphCanvasSkills: GraphCanvasFetchers['searchGraphCanvasSkills'] = async ({
        acceptLanguage,
        limit,
        searchTerm,
    }) => {
        return validatingCobrainerApiFetcher.get(skillShortSchema.array(), {
            pathname: '/skills',
            requestOptions: {
                headers: { 'Accept-Language': acceptLanguage },
                searchParams: { limit, searchTerm, type: ['hard', 'soft', 'language'].join(',') },
            },
        });
    };

    const fetchGraphCanvasUser: GraphCanvasFetchers['fetchGraphCanvasUser'] = async ({ userId }) => {
        return validatingCobrainerApiFetcher.get(userSchema, { pathname: `/users/${userId}` });
    };

    const fetchGraphCanvasUserSkills: GraphCanvasFetchers['fetchGraphCanvasUserSkills'] = async ({
        acceptLanguage,
        userId,
    }) => {
        return validatingCobrainerApiFetcher.get(endorsedHardSoftSkillArraySchema, {
            pathname: `/users/${userId}/hard-soft-skills`,
            requestOptions: { headers: { 'Accept-Language': acceptLanguage } },
        });
    };

    const fetchGraphCanvasUsers: GraphCanvasFetchers['fetchGraphCanvasUsers'] = async ({ page, size }) => {
        return validatingCobrainerApiFetcher.get(userPaginationSchema, {
            pathname: '/users',
            requestOptions: { searchParams: { page, size } },
        });
    };

    const searchGraphCanvasUsers: GraphCanvasFetchers['searchGraphCanvasUsers'] = async ({
        page,
        searchTerm,
        size,
    }) => {
        return validatingCobrainerApiFetcher.get(globalSearchUserResponseSchema, {
            pathname: '/global/search/users',
            requestOptions: { searchParams: { page, searchTerm, size } },
        });
    };

    const fetchGraphCanvasJob: GraphCanvasFetchers['fetchGraphCanvasJob'] = async ({ acceptLanguage, jobId }) => {
        return validatingCobrainerApiFetcher.get(jobSchema, {
            pathname: `/jobs/${jobId}`,
            requestOptions: { headers: { 'Accept-Language': acceptLanguage } },
        });
    };

    const fetchGraphCanvasJobs: GraphCanvasFetchers['fetchGraphCanvasJobs'] = async ({
        acceptLanguage,
        page,
        size,
    }) => {
        return validatingCobrainerApiFetcher.postJson(jobPaginatedSchema, {
            pathname: '/jobs/filter',
            payload: { paged: { page, size } },
            requestOptions: { headers: { 'Accept-Language': acceptLanguage } },
        });
    };

    const searchGraphCanvasJobs: GraphCanvasFetchers['searchGraphCanvasJobs'] = async ({ page, searchTerm, size }) => {
        return validatingCobrainerApiFetcher.get(globalSearchJobResponseSchema, {
            pathname: '/global/search/jobs',
            requestOptions: { searchParams: { page, searchTerm, size } },
        });
    };

    const fetchGraphCanvasJobRoles: GraphCanvasFetchers['fetchGraphCanvasJobRoles'] = async ({
        acceptLanguage,
        architectureId,
        clusterId,
    }) => {
        return validatingCobrainerApiFetcher.get(jobRoleWithTypeSchemaTransformed.array(), {
            pathname: `/job-architectures/${architectureId}/clusters/${clusterId}/roles`,
            requestOptions: {
                headers: { 'Accept-Language': acceptLanguage },
                searchParams: { includeProfiles: true },
            },
        });
    };

    const fetchGraphCanvasJobLevels: GraphCanvasFetchers['fetchGraphCanvasJobLevels'] = async ({
        acceptLanguage,
        architectureId,
        roleId,
    }) => {
        return validatingCobrainerApiFetcher.get(jobLevelWithTypeSchemaTransformed.array(), {
            pathname: `/job-architectures/${architectureId}/roles/${roleId}/levels`,
            requestOptions: {
                headers: { 'Accept-Language': acceptLanguage },
                searchParams: { includeProfiles: true },
            },
        });
    };

    return {
        fetchGraphCanvasJob,
        fetchGraphCanvasJobLevels,
        fetchGraphCanvasJobRoles,
        fetchGraphCanvasJobs,
        fetchGraphCanvasRelatedSkills,
        fetchGraphCanvasUser,
        fetchGraphCanvasUsers,
        fetchGraphCanvasUserSkills,
        searchGraphCanvasJobs,
        searchGraphCanvasSkills,
        searchGraphCanvasUsers,
    };
};
